import {
  useMutation,
  UseMutationOptions,
  UseMutationResult,
  useQueryClient,
} from '@tanstack/react-query';
import { AxiosError } from 'axios';

import { axiosInstance } from '../../axios';
import {
  DeleteLanguagesApiResponse,
  DeleteLanguagesResult,
  LanguageDataDocumentRequest,
} from '../../types';

export const LANGUAGES_QUERY_KEY = 'languages';

type CreateParams = { data: LanguageDataDocumentRequest };
type UpdateParams = { id: string; data: LanguageDataDocumentRequest };
/** 1..N id — usunięcie pojedyncze to tablica jednoelementowa. */
type DeleteParams = { ids: string[] };

// Cały CRUD słownika języków idzie przez backend (codebase `admin`, admin SDK):
// klient nie ma prawa zapisu do kolekcji `languages`.
export const useCreateLanguage = (
  options?: UseMutationOptions<void, AxiosError, CreateParams>,
): UseMutationResult<void, AxiosError, CreateParams> => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, CreateParams>({
    mutationFn: async ({ data }) => {
      await axiosInstance.post('/v3-createLanguage', data);
    },
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [LANGUAGES_QUERY_KEY] });
      options?.onSuccess?.(...args);
    },
  });
};

export const useUpdateLanguage = (
  options?: UseMutationOptions<void, AxiosError, UpdateParams>,
): UseMutationResult<void, AxiosError, UpdateParams> => {
  const queryClient = useQueryClient();

  return useMutation<void, AxiosError, UpdateParams>({
    mutationFn: async ({ id, data }) => {
      await axiosInstance.put('/v3-updateLanguage', { id, name: data.name, code: data.code });
    },
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [LANGUAGES_QUERY_KEY] });
      options?.onSuccess?.(...args);
    },
  });
};

// Single i bulk tym samym endpointem — body przy DELETE leci przez `config.data`.
export const useDeleteLanguages = (
  options?: UseMutationOptions<DeleteLanguagesResult, AxiosError, DeleteParams>,
): UseMutationResult<DeleteLanguagesResult, AxiosError, DeleteParams> => {
  const queryClient = useQueryClient();

  return useMutation<DeleteLanguagesResult, AxiosError, DeleteParams>({
    mutationFn: async ({ ids }) => {
      const res = await axiosInstance.delete<DeleteLanguagesApiResponse>('/v3-deleteLanguages', {
        data: { ids },
      });
      return res.data.data;
    },
    ...options,
    onSuccess: (...args) => {
      queryClient.invalidateQueries({ queryKey: [LANGUAGES_QUERY_KEY] });
      options?.onSuccess?.(...args);
    },
  });
};
